import { MAX_SERIES, type CsvFormat } from './compare'
import { toLocalInput } from './time'
import { windowRange, type WindowValue } from './timeWindow'
import { serverStorage } from './userDocuments'

export const SAVED_VIEWS_KEY = 'hydro:comparison-views:v1'
export const MAX_SAVED_VIEWS = 20

interface IdEntropy {
  randomUUID?: () => string
  getRandomValues?: (array: Uint32Array) => Uint32Array
}
let fallbackSequence = 0

/** Browser-local identifiers carry no authority or secrets. randomUUID is
 *  absent on HTTP previews, while getRandomValues remains available there. */
export function createSavedViewId(entropy: IdEntropy | undefined = globalThis.crypto): string {
  try {
    if (typeof entropy?.randomUUID === 'function') return entropy.randomUUID()
  } catch { /* An unavailable crypto method must not prevent saving a view. */ }
  try {
    if (typeof entropy?.getRandomValues === 'function') {
      const parts = entropy.getRandomValues(new Uint32Array(4))
      return `view-${Array.from(parts, (part) => part.toString(16).padStart(8, '0')).join('')}`
    }
  } catch { /* A restricted browser can still keep these nonsecurity tokens. */ }
  fallbackSequence += 1
  return `view-${Date.now().toString(36)}-${fallbackSequence.toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** A view stores the query, never a copy or a transformation of its data.
 *  Presets keep their chosen instant so reopening a review is reproducible. */
export interface SavedComparisonView {
  id: string
  name: string
  pairs: string[]
  window: WindowValue
  panelOpen: boolean
  csvFormat: CsvFormat
  savedAt: number
}

type Store = Pick<Storage, 'getItem' | 'setItem'>
function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function validInstant(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
    && value > 0 && !Number.isNaN(new Date(value).getTime())
}

function readWindow(value: unknown): WindowValue | null {
  if (!record(value)) return null
  if (value.kind === 'preset' && ['24h', '7d', '30d', '1y'].includes(String(value.preset))
      && validInstant(value.at)) {
    return { kind: 'preset', preset: value.preset as '24h' | '7d' | '30d' | '1y', at: value.at }
  }
  if (value.kind !== 'custom' || typeof value.fromText !== 'string'
      || typeof value.toText !== 'string') return null
  const exactMinute = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
  if (!exactMinute.test(value.fromText) || !exactMinute.test(value.toText)) return null
  const win: WindowValue = { kind: 'custom', fromText: value.fromText, toText: value.toText }
  const range = windowRange(win)
  // A corrupt date such as 31 February must not roll into another month.
  return range.valid && toLocalInput(range.from) === win.fromText
    && toLocalInput(range.to) === win.toText ? win : null
}

function readView(value: unknown): SavedComparisonView | null {
  if (!record(value) || typeof value.id !== 'string' || !value.id || value.id.length > 100
      || typeof value.name !== 'string' || !value.name.trim() || value.name.trim().length > 80
      || !Array.isArray(value.pairs) || !value.pairs.length
      || typeof value.panelOpen !== 'boolean'
      || (value.csvFormat !== 'csv' && value.csvFormat !== 'csv-excel')
      || !validInstant(value.savedAt)) return null
  if (!value.pairs.every((id): id is string => typeof id === 'string' && id.length <= 512
      && /^[^|,\s]+\|[^|,\s]+$/.test(id))) return null
  const pairs = [...new Set(value.pairs as string[])]
  if (pairs.length > MAX_SERIES) return null
  const window = readWindow(value.window)
  if (!window) return null
  return { id: value.id, name: value.name.trim(), pairs, window,
    panelOpen: value.panelOpen, csvFormat: value.csvFormat, savedAt: value.savedAt }
}

/** Local storage is editable and may belong to an older application build.
 *  Invalid views are ignored as a whole, never restored as a different query. */
export function parseSavedViews(raw: string | null): SavedComparisonView[] {
  if (!raw) return []
  try {
    const payload: unknown = JSON.parse(raw)
    if (!record(payload) || payload.version !== 1 || !Array.isArray(payload.views)) return []
    const ids = new Set<string>()
    const names = new Set<string>()
    const views: SavedComparisonView[] = []
    for (const candidate of payload.views) {
      const view = readView(candidate)
      if (!view || ids.has(view.id) || names.has(view.name.toLocaleLowerCase())) continue
      views.push(view)
      ids.add(view.id)
      names.add(view.name.toLocaleLowerCase())
      if (views.length === MAX_SAVED_VIEWS) break
    }
    return views
  } catch { return [] }
}

export function readSavedViews(storage?: Store): SavedComparisonView[] {
  try { return parseSavedViews((storage ?? serverStorage).getItem(SAVED_VIEWS_KEY)) }
  catch { return [] }
}
